import classNames from "classnames";
import Currency from "@autocx/forms/src/controls/currency";
import {Disclosure} from '@headlessui/react'
import AnimateHeight from "react-animate-height";
import {Fragment, useContext, useEffect, useMemo, useRef, useState} from "react";
import {WebsiteContext} from "../../index";
import {aspectRatio, backgroundColour, backgroundPattern} from "../../utils/block-utils";
import Block from "../block";
import {SwiperElement} from "../../components/swiper-element";
import throttle from "lodash/throttle";
import useDetectKeyboardOpen from "../../hooks/useDetectKeyboardOpen";
import {Accordion, getProductFields, ProductCtaContainer} from "./index";
import {EnquiryActionBanner, EnquiryPurchaseMethod} from "./enquiry-purchase-method";
import BidSalesEvent from "./bid-sales-event";
import axios from "axios";
import PricingFields from "./components/pricing-fields";
import WebsiteFieldRenderer from "../../components/website-field-renderer";
import {Gallery} from "react-photoswipe-gallery";
import {subscribeToPublicSalesEvents, reconnectPublicSalesEvents} from "../../realtime/public-sales-events";
import Confetti from "react-dom-confetti";
import confettiConfig from "./components/confetti-config";
import {applyLotProductSnapshot, buildLotProductSnapshotPath} from '../../utils/lot-product-snapshot';
import {
    appendCatalogueNumberQuery,
    fetchPublicCatalogueLotSummary,
    getPublicProductIdentifier,
    isPublicSalesEventMessageForCurrentLot,
    isPublicSalesEventStateCurrent,
    mergePublicSalesEventSummary,
    mergePublicProductProps,
    mergeRefreshedPublicProduct,
    sanitizePublicProduct,
    shouldRefreshPublicBidHistoryForInvalidation,
    shouldRefreshPublicBidHistoryForSummary
} from "../../realtime/public-sales-event-product-state";

const galleryOptions = {
    shareEl: false,
    mainClass: 'product-gallery-photoswipe',
    showHideOpacity: false,
    showAnimationDuration: 333,
    hideAnimationDuration: 333,
    maxSpreadZoom: 4,
    fullscreenEl: false,
    getDoubleTapZoom: function (isMouseClick, item) {
        if (isMouseClick) {
            return 2
        } else {
            return item.initialZoomLevel < 0.7 ? 1 : 4;
        }
    }
}

function hasAuctionSalesEvent(salesEvent) {
    return salesEvent?.sellingMethod?.toLowerCase() === 'auction';
}

function hasResolvedAuctionLot(salesEvent) {
    const lotStatus = String(salesEvent?.lotStatus || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    return hasAuctionSalesEvent(salesEvent)
        && (lotStatus === 'sold' || lotStatus === 'passedin' || lotStatus === 'salefellthrough');
}

export default function Design2(props) {
    const context = useContext(WebsiteContext);
    const isAuthenticatedCustomer = context?.customerSession?.isAuthenticated === true;
    // Older auction sites can allow sign-in without enabling the account navigation.
    // Their renderer does not provide a session marker, so retain the profile lookup.
    const isKnownGuest = context?.website?.store?.accountManagementEnabled === true && !isAuthenticatedCustomer;
    const headerBar = useRef();
    const galleryIndexRef = useRef(0);
    const swiperRef = useRef(null);
    const [profile, setProfile] = useState(false);
    const [isProfileLoaded, setIsProfileLoaded] = useState(() => !props?.values?.salesEvent || isKnownGuest);
    const realtimeHandlersRef = useRef(null);
    const [values, setValues] = useState(() => sanitizePublicProduct(props?.values));
    const publicProductIdentifier = getPublicProductIdentifier(
        values,
        getPublicProductIdentifier(props?.values)
    );
    const catalogueNumber = values?.catalogueContext?.catalogueNumber;
    const [isScrolledUnder, setIsScrolledUnder] = useState(false);
    const isKeyboardOpen = useDetectKeyboardOpen()
    const productFields = getProductFields(values?.productTemplate?.userDefinedFieldsets, values)
    // flattendedProductFields uses userDefinedFieldsets directly instead of the getProductFields function to ignore hidden fields
    const flattendedProductFields = values?.productTemplate?.userDefinedFieldsets?.reduce((acc, cur) => acc.concat(cur.fields || []), []);
    const [usersBid, setUsersBid] = useState(props?.values?.bids?.[0] ?? {})
    const [isUsersBidLoaded, setIsUsersBidLoaded] = useState(false);
    const [publicBidHistory, setPublicBidHistory] = useState([]);
    const [isPublicBidHistoryLoading, setIsPublicBidHistoryLoading] = useState(false);
    const [winnerConfettiActive, setWinnerConfettiActive] = useState(false);
    const purchaseMethod = values?.productTemplate?.purchaseMethod;
    const hasSalesEvent = !!values?.salesEvent;
    const isAuctionSalesEvent = hasAuctionSalesEvent(values?.salesEvent);
    const hasSellingPrice = Number(values?.sellingPrice) > 0 || Number(values?.discountedSellingPrice) > 0;
    const shouldShowPublicBidHistory = isAuctionSalesEvent;
    const hasImages = Array.isArray(values?.images) && values.images.length > 0;
    const isBidderOutcomeReady = isProfileLoaded && (!profile?.customer?.id || isUsersBidLoaded);
    const refreshTimeoutRef = useRef(null);
    const latestSummaryRef = useRef(null);
    const privateRequestRef = useRef(null);
    const privateScope = `${publicProductIdentifier}:${values?.salesEvent?.lotId}:${catalogueNumber}:${profile?.customer?.id}`;
    const privateScopeRef = useRef(privateScope);
    privateScopeRef.current = privateScope;

    function refreshPrivateOutcome(salesEvent = salesEventRef.current, force = false) {
        if (!profile?.customer?.id || !salesEvent) return Promise.resolve();
        const key = `${privateScope}:${salesEvent.publicStateVersion}:${salesEvent.lotStatus}`;
        const previous = privateRequestRef.current;
        if (previous?.key === key
            && previous.sequence === bidderRequestSequenceRef.current
            && (previous.pending || !force)) return previous.promise;
        const bidderRequestSequence = ++bidderRequestSequenceRef.current;
        const request = {key, pending: true, sequence: bidderRequestSequence};
        privateRequestRef.current = request;
        setIsUsersBidLoaded(false);
        request.promise = axios.get(appendCatalogueNumberQuery(
            `/api/v2/products/${publicProductIdentifier}/my-bids`, catalogueNumber))
            .then(({data}) => {
                if (requestIdentityRef.current !== requestIdentity
                    || bidderRequestSequence !== bidderRequestSequenceRef.current
                    || privateRequestRef.current !== request
                    || privateScopeRef.current !== privateScope) return;
                setUsersBid(Array.isArray(data) ? (data[0] ?? {}) : {});
                setIsUsersBidLoaded(true);
                request.pending = false;
            }).catch(error => {
                if (privateRequestRef.current === request) privateRequestRef.current = null;
                console.log(error);
            });
        return request.promise;
    }
    const salesEventRef = useRef(values?.salesEvent);
    const refreshSequenceRef = useRef(0);
    const bidderRequestSequenceRef = useRef(0);
    const historyRequestSequenceRef = useRef(0);
    const requestIdentity = JSON.stringify([getPublicProductIdentifier(props?.values), props?.values?.salesEvent?.lotId, props?.values?.catalogueContext?.catalogueNumber, profile?.customer?.id]);
    const requestIdentityRef = useRef(requestIdentity);
    requestIdentityRef.current = requestIdentity;
    const winnerCelebrationRef = useRef({
        lotId: values?.salesEvent?.lotId,
        resolvedAtStart: String(values?.salesEvent?.lotStatus || '').trim().toLowerCase() === 'sold',
        celebrated: false
    });

    useEffect(() => {
        setValues((currentValues) => mergePublicProductProps(currentValues, props?.values));
    }, [props?.values]);

    useEffect(() => {
        requestIdentityRef.current = requestIdentity;
        latestSummaryRef.current = null;
        return () => {
            if (requestIdentityRef.current === requestIdentity) requestIdentityRef.current = null;
            refreshSequenceRef.current += 1;
            bidderRequestSequenceRef.current += 1;
            historyRequestSequenceRef.current += 1;
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        };
    }, [requestIdentity]);

    useEffect(() => {
        salesEventRef.current = values?.salesEvent;
    }, [values?.salesEvent]);

    // One detail request when a watched lot settles; no polling or catalogue-wide
    // snapshot payload. SSR has already loaded it when opening an ended lot.
    useEffect(() => {
        const path = buildLotProductSnapshotPath(values, catalogueNumber);
        const lotId = values?.salesEvent?.lotId;
        if (!path || String(values?.snapshotCheckedLotId) === String(lotId)) return;
        let active = true;
        axios.get(path).then(({data}) => {
            if (!active) return;
            setValues(current => String(current?.salesEvent?.lotId) === String(lotId)
                ? {...applyLotProductSnapshot(current, data), snapshotCheckedLotId: lotId}
                : current);
        }).catch(error => {
            if (!active) return;
            if (error?.response?.status === 404) {
                setValues(current => String(current?.salesEvent?.lotId) === String(lotId)
                    ? {...current, snapshotCheckedLotId: lotId} : current);
            } else console.log(error);
        });
        return () => { active = false; };
    }, [values?.salesEvent?.lotId, values?.salesEvent?.lotStatus, catalogueNumber]);

    useEffect(() => {
        setUsersBid(values?.bids?.[0] ?? {});
        setIsUsersBidLoaded(false);
        privateRequestRef.current = null;
        latestSummaryRef.current = null;
    }, [publicProductIdentifier, values?.salesEvent?.lotId, catalogueNumber]);

    function loadPublicBidHistory(requestConfig = {}) {
        const requestSequence = ++historyRequestSequenceRef.current;
        const isCurrentRequest = () => requestIdentityRef.current === requestIdentity && requestSequence === historyRequestSequenceRef.current;
        if (!publicProductIdentifier || !shouldShowPublicBidHistory) {
            setPublicBidHistory([]);
            return Promise.resolve([]);
        }

        const bidHistoryPath = appendCatalogueNumberQuery(
            `/api/v2/products/${publicProductIdentifier}/bid-history`,
            catalogueNumber
        );
        return axios.get(bidHistoryPath, requestConfig)
            .then((response) => {
                if (!isCurrentRequest()) return [];
                const history = Array.isArray(response?.data) ? response.data : [];
                setPublicBidHistory(history);
                return history;
            })
            .catch((err) => {
                if (!isCurrentRequest()) return [];
                if (!axios.isCancel(err)) {
                    console.log(err);
                    setPublicBidHistory([]);
                }
                return [];
            });
    }

    function refreshPublicBidHistory() {
        setIsPublicBidHistoryLoading(true);

        const request = loadPublicBidHistory();
        const requestSequence = historyRequestSequenceRef.current;
        return request
            .finally(() => {
                if (requestIdentityRef.current !== requestIdentity || requestSequence !== historyRequestSequenceRef.current) return;
                setIsPublicBidHistoryLoading(false);
            });
    }

    function handleBidSuccess(bid) {
        bidderRequestSequenceRef.current += 1;
        setUsersBid(p => ({...p, ...bid}))
        // A bid POST can finish after the public lot has already resolved. Its response
        // confirms acceptance, not who won, and invalidates any earlier private lookup.
        const lotHasResolved = hasResolvedAuctionLot(salesEventRef.current)
            || hasResolvedAuctionLot(latestSummaryRef.current);
        setIsUsersBidLoaded(!lotHasResolved);
        if (lotHasResolved) scheduleRefresh();
        refreshPublicBidHistory();
    }

    function refreshSalesEventState() {
        if (requestIdentityRef.current !== requestIdentity) return Promise.resolve();
        if (!publicProductIdentifier) return Promise.resolve();
        const requestSequence = ++refreshSequenceRef.current;
        const bidderSequenceAtStart = bidderRequestSequenceRef.current;
        const lotId = salesEventRef.current?.lotId;
        const isCurrentRequest = () => requestIdentityRef.current === requestIdentity && requestSequence === refreshSequenceRef.current;
        reconnectPublicSalesEvents().catch(() => undefined);

        return axios.get(`/api/v2/products/${publicProductIdentifier}`)
            .then(async ({data}) => {
                if (!isCurrentRequest()) return;
                let catalogueSummary = null;

                if (catalogueNumber) {
                    try {
                        catalogueSummary = await fetchPublicCatalogueLotSummary({
                            catalogueNumber,
                            lotId,
                            productIdentifier: publicProductIdentifier
                        });
                    } catch (error) {
                        console.log(error);
                    }
                }

                if (!isCurrentRequest()) return;

                const hasSalesEventContext = !!data?._links?.salesEventLot
                    || !!catalogueNumber
                    || !!salesEventRef.current?.lotId;
                const refreshedSalesEvent = mergeRefreshedPublicProduct(
                    {...values, salesEvent: salesEventRef.current},
                    data,
                    catalogueSummary ?? latestSummaryRef.current
                )?.salesEvent;
                const outcomeNeedsBidderRefresh = !hasResolvedAuctionLot(salesEventRef.current)
                    && hasResolvedAuctionLot(refreshedSalesEvent);
                const shouldRefreshBidderContext = !!profile?.customer?.id && hasSalesEventContext
                    && (bidderSequenceAtStart === bidderRequestSequenceRef.current
                        || outcomeNeedsBidderRefresh || hasResolvedAuctionLot(refreshedSalesEvent));
                const currentLotStatus = String(salesEventRef.current?.lotStatus || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
                const currentLotIsResolved = currentLotStatus === 'sold'
                    || currentLotStatus === 'passedin'
                    || currentLotStatus === 'salefellthrough';
                if (shouldRefreshBidderContext && !currentLotIsResolved) {
                    setIsUsersBidLoaded(false);
                }

                setValues((currentValues) => mergeRefreshedPublicProduct(
                    currentValues,
                    data,
                    catalogueSummary ?? latestSummaryRef.current
                ));

                if (!shouldRefreshBidderContext) return;

                await refreshPrivateOutcome(
                    refreshedSalesEvent ?? catalogueSummary ?? latestSummaryRef.current ?? salesEventRef.current,
                    true
                );
            })
            .catch((error) => {
                console.log(error);
            });
    }

    useEffect(() => {
        const lotId = values?.salesEvent?.lotId;
        const lotIsSold = String(values?.salesEvent?.lotStatus || '').trim().toLowerCase() === 'sold';
        const currentUserWon = lotIsSold && !!usersBid?.isWinner;
        const trackedCelebration = winnerCelebrationRef.current;

        if (String(trackedCelebration.lotId ?? '') !== String(lotId ?? '')) {
            winnerCelebrationRef.current = {
                lotId,
                resolvedAtStart: lotIsSold,
                celebrated: false
            };
            setWinnerConfettiActive(false);
            return undefined;
        }

        if (trackedCelebration.resolvedAtStart || trackedCelebration.celebrated || !currentUserWon) {
            return undefined;
        }

        trackedCelebration.celebrated = true;
        const prefersReducedMotion = typeof window !== 'undefined'
            && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
            return undefined;
        }

        setWinnerConfettiActive(true);
        const timeout = setTimeout(() => setWinnerConfettiActive(false), 1000);
        return () => clearTimeout(timeout);
    }, [usersBid?.isWinner, values?.salesEvent?.lotId, values?.salesEvent?.lotStatus]);

    function scheduleRefresh() {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
            refreshSalesEventState();
        }, 150 + Math.round(Math.random() * 150));
    }

    useEffect(() => {
        const navHeight = document.getElementById('block_navigation')?.getBoundingClientRect()?.height;

        function handleScroll() {
            const headerBarScrollPos = headerBar?.current?.getBoundingClientRect().height + headerBar?.current?.getBoundingClientRect().top - navHeight;
            // Check if the scrollable element is under the fixed element
            setIsScrolledUnder((headerBarScrollPos < 0))
        }

        const throttledHandleScroll = throttle(handleScroll, 250);
        document.addEventListener("scroll", throttledHandleScroll)

        return () => {
            document.removeEventListener(
                "scroll",
                throttledHandleScroll
            );
        };
    }, [headerBar.current]);

    // Get current profile
    useEffect(() => {
        if (!hasSalesEvent || isKnownGuest) {
            setProfile(false);
            setIsProfileLoaded(true);
            return undefined;
        }

        const cancelToken = axios.CancelToken.source();
        setIsProfileLoaded(false);

        axios.get('/api/v2/publicusers/profile', {cancelToken: cancelToken.token})
            .then((response) => {
                setProfile(response.data);
                setIsProfileLoaded(true);
            })
            .catch((err) => {
                if (!axios.isCancel(err)) {
                    console.log(err);
                    setIsProfileLoaded(true);
                }
            })
        return () => cancelToken.cancel();
    }, [hasSalesEvent, isAuthenticatedCustomer, isKnownGuest])

    // Public-version changes trigger coalesced bidder lookups without polling.
    useEffect(() => {
        if (!publicProductIdentifier || !values?.salesEvent) return;
        if (!profile?.customer?.id) return;
        refreshPrivateOutcome();
    }, [profile?.customer?.id, publicProductIdentifier, values?.salesEvent?.lotId, catalogueNumber,
        values?.salesEvent?.publicStateVersion,
        hasResolvedAuctionLot(values?.salesEvent), requestIdentity]);

    realtimeHandlersRef.current = {
            onSummary: (message) => {
                const summary = message?.summary;
                if (requestIdentityRef.current !== requestIdentity) return;
                if (!summary || !isPublicSalesEventMessageForCurrentLot(salesEventRef.current, summary)) return;
                if (!isPublicSalesEventStateCurrent(salesEventRef.current, summary)) return;
                if (latestSummaryRef.current && !isPublicSalesEventStateCurrent(latestSummaryRef.current, summary)) return;
                latestSummaryRef.current = summary;

                const bidHistoryMayHaveChanged = shouldRefreshPublicBidHistoryForSummary(salesEventRef.current, summary);
                const summarySalesEvent = mergePublicSalesEventSummary(salesEventRef.current, summary);
                const bidderOutcomeNeedsRefresh = !!profile?.customer?.id
                    && !hasResolvedAuctionLot(salesEventRef.current)
                    && hasResolvedAuctionLot(summarySalesEvent);

                if (bidderOutcomeNeedsRefresh) {
                    setIsUsersBidLoaded(false);
                    // Updating the terminal version below triggers the coalesced private
                    // bidder lookup. The summary already contains the committed public state,
                    // so a second public REST refresh would only duplicate the request.
                }

                salesEventRef.current = summarySalesEvent;
                setValues((currentValues) => sanitizePublicProduct({
                    ...currentValues,
                    salesEvent: mergePublicSalesEventSummary(currentValues?.salesEvent, summary)
                }));

                if (shouldShowPublicBidHistory && bidHistoryMayHaveChanged) {
                    refreshPublicBidHistory();
                }
            },
            onInvalidation: (message) => {
                if (!isPublicSalesEventMessageForCurrentLot(salesEventRef.current, message)) return;
                if (!isPublicSalesEventStateCurrent(salesEventRef.current, message)) return;
                if (String(latestSummaryRef.current?.publicStateVersion ?? '') !== String(message.publicStateVersion ?? '')
                    || !message.publicStateVersion) scheduleRefresh();
                if (shouldShowPublicBidHistory && shouldRefreshPublicBidHistoryForInvalidation(salesEventRef.current, message)) {
                    refreshPublicBidHistory();
                }
            },
            onReconnect: () => {
                scheduleRefresh();
            }
    };

    useEffect(() => {
        if (!publicProductIdentifier || !values?.salesEvent) return;
        return subscribeToPublicSalesEvents(publicProductIdentifier, {
            lotId: isAuctionSalesEvent ? values.salesEvent.lotId : null,
            onSummary: message => realtimeHandlersRef.current.onSummary(message),
            onInvalidation: message => realtimeHandlersRef.current.onInvalidation(message),
            onReconnect: () => realtimeHandlersRef.current.onReconnect()
        });
    }, [publicProductIdentifier, values?.salesEvent?.lotId, catalogueNumber, isAuctionSalesEvent]);

    useEffect(() => () => {
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    }, []);

    useEffect(() => {
        const cancelToken = axios.CancelToken.source();

        if (!publicProductIdentifier || !shouldShowPublicBidHistory) {
            setPublicBidHistory([]);
            setIsPublicBidHistoryLoading(false);
            return () => cancelToken.cancel();
        }

        setIsPublicBidHistoryLoading(true);
        loadPublicBidHistory({cancelToken: cancelToken.token})
            .finally(() => {
                if (!cancelToken.token.reason) {
                    setIsPublicBidHistoryLoading(false);
                }
            });

        return () => cancelToken.cancel();
    }, [
        publicProductIdentifier,
        values?.salesEvent?.lotId,
        values?.salesEvent?.lotStatus,
        values?.salesEvent?.secondsRemaining,
        values?.salesEvent?.secondsToStart,
        catalogueNumber,
        shouldShowPublicBidHistory
    ])

    const swiperProps = useMemo(() => {
        const images = Array.isArray(values?.images) ? values.images : [];
        const imageState = Array.isArray(values?.imagesState)
            ? values.imagesState
            : Array.isArray(values?.imageState)
                ? values.imageState
                : [];

        return {
            video: values?.video,
            photoSwipe: hasImages,
            photoSwipeAspectRatio: context?.website?.store?.productCardAspectRatio,
            lazyRootMargin: "300px 300px",
            onSwiperReady: (swiper) => {
                swiperRef.current = swiper;
            },
            values: {
                items: images.map((image, i) => {
                    const obj = {image}
                    obj.imagesState = imageState[i] ? {...imageState[i]} : {};
                    obj.originalWidth = 2000;
                    obj.responsiveWidths = {
                        mobile: 360,
                        tablet: 640,
                        desktop: 720
                    };
                    return obj;
                }),
                swiperOptions: {
                    loop: false,
                    slidesPerView: 1,
                    spaceBetween: 0,
                    breakpoints: {
                        640: {
                            slidesPerView: 2,
                            spaceBetween: 2,
                        },
                    },
                },
            },
            swiperSlideClassName: aspectRatio({values: {options: {imageAspectRatio: context?.website?.store?.productCardAspectRatio}}}),
        }
    }, [context?.website?.store?.productCardAspectRatio, hasImages, values?.imageState, values?.images, values?.imagesState, values?.uuid, values?.video])

    const onOpen = (pswpInstance) => {
        let lastGalleryIndex = pswpInstance.getCurrentIndex();

        const updateGalleryIndex = () => {
            galleryIndexRef.current = pswpInstance.getCurrentIndex();
        };

        updateGalleryIndex();
        pswpInstance.listen('afterChange', () => {
            updateGalleryIndex();
            if (galleryIndexRef.current === lastGalleryIndex) return;

            lastGalleryIndex = galleryIndexRef.current;
            if (swiperRef.current) {
                swiperRef.current.slideTo(galleryIndexRef.current);
            }
        });
    }

    return (
        <Gallery options={galleryOptions} onOpen={onOpen}>
            <div className="pointer-events-none fixed left-1/2 top-1/2 z-[70]" aria-hidden="true">
                <Confetti active={winnerConfettiActive} config={confettiConfig}/>
            </div>
            <Block {...props}
                   className={classNames(
                       props.className,
                       !values?.options?.inset ? backgroundPattern(props) : null,
                       !values?.options?.inset ? backgroundColour(values.options?.backgroundColour) : null)
                   }>
            <SwiperElement {...swiperProps}/>
            <header className="section-grey">
                <div className={"section mx-auto max-w-7xl grid-cols-1 sm:grid sm:grid-cols-3"}>
                    <h1 className={classNames("!mb-0 !text-3xl sm:!text-4xl col-span-2 rich-text")}
                        dangerouslySetInnerHTML={{__html: values.title}}
                        data-title=""
                    />
                </div>
            </header>
            {/* Inline mobile purchase method banner */}
            <ProductCtaContainer bannerRef={headerBar} className={"h-auto min-h-20 flex-col"}>
                {values.salesEvent &&
                    <>
                        <div className={"flex flex-row w-full justify-between items-center"}>
                            <BidSalesEvent
                                profile={profile}
                                productUuid={publicProductIdentifier}
                                salesEvent={values.salesEvent}
                                onBidSuccess={handleBidSuccess}
                                onRefreshRequested={refreshSalesEventState}
                                usersBid={usersBid}
                                variant="full"
                                bidButtonClasses={"h-12"}
                                publicBidHistory={publicBidHistory}
                                isPublicBidHistoryLoading={isPublicBidHistoryLoading}
                                showPublicBidHistory={shouldShowPublicBidHistory}
                                onBidHistoryRequested={refreshPublicBidHistory}
                                isBidderOutcomeReady={isBidderOutcomeReady}
                            />
                        </div>
                        <hr className={"my-4 mx-0 w-full"}/>
                    </>
                }
                {purchaseMethod === 'enquiry' &&
                    <div className={classNames(
                        "flex flex-row w-full items-center",
                        !values?.productTemplate?.hidePriceOnWebsite && (values.sellingPrice > 0 || !values.salesEvent) ? "justify-between" : "justify-center"
                    )}>
                        <EnquiryActionBanner {...props} context={context} salesEvent={values.salesEvent}/>
                    </div>
                }
                {values?.productTemplate?.pricingFields && values?.productTemplate?.pricingFields.length > 0 ? (
                    <>
                        <hr className="my-4 mx-0 w-full"/>
                        <PricingFields
                            pricingFields={values.productTemplate.pricingFields}
                            productFields={flattendedProductFields}
                            productValues={values}
                        />
                    </>
                ) : null}
            </ProductCtaContainer>
            <div className={classNames("mx-auto max-w-7xl section flex flex-col items-center !pt-0")}>
                <div className={classNames("w-full grid-cols-1 py-4 inline-flex flex-col sm:grid sm:grid-cols-3")}>
                    <div className="text-left col-span-2 order-last sm:order-first sm:mr-8">
                        {productFields?.map((fieldset, i) => {
                            if (values?.productTemplateUuid?.includes('car') && fieldset?.id === 'features') {
                                return (
                                    <Disclosure key={fieldset.id} id={fieldset.id} as="div"
                                                className={"flex flex-col border-opacity-25"}>
                                        {({open}) => (
                                            <>
                                                <Disclosure.Button
                                                    className="w-full flex justify-between items-start pt-5">
                                                    <h3 className={classNames("!text-xl")}>
                                                        {fieldset.label}
                                                    </h3>
                                                    <span className="ml-6 h-7 flex items-center">
                                                        <span className={classNames(
                                                            "border-current h-6 w-6",
                                                            open ? "chevron--up" : "chevron--down"
                                                        )}>
                                                            <svg width="24" height="24" version="1.1"
                                                                 viewBox="0 0 100 100"
                                                                 xmlns="http://www.w3.org/2000/svg"
                                                                 preserveAspectRatio="xMidYMid"
                                                                 stroke="currentColor">
                                                                <g className="chevron__container">
                                                                    <line className="chevron__line1" x1="10"
                                                                          y1="50" x2="50" y2="50"/><line
                                                                    className="chevron__line2" x1="90" y1="50"
                                                                    x2="50" y2="50"/>
                                                                </g>
                                                            </svg>
                                                        </span>
                                                        </span>
                                                </Disclosure.Button>
                                                <AnimateHeight
                                                    id={fieldset.id}
                                                    height={open ? 'auto' : 0}
                                                    animateOpacity={true}>
                                                    <Disclosure.Panel as="div" static
                                                                      className={classNames("mb-8 -mt-1.5 rich-text")}>
                                                        {fieldset.fields?.map((field, i) => (
                                                            <div key={field.id + i}
                                                                 className="pt-2 whitespace-pre-line">
                                                                {field.prefix} {values[field.id] || '—'} {field.suffix}
                                                            </div>
                                                        ))}
                                                    </Disclosure.Panel>
                                                </AnimateHeight>
                                            </>
                                        )}
                                    </Disclosure>
                                )
                            }
                            return (
                                <div
                                    className={classNames("w-full pt-6 inline-flex flex-col grid grid-cols-1 gap-x-10 items-start sm:grid-cols-3")}
                                    key={fieldset.id}>
                                    {fieldset.label && <div className="text-left col-span-1">
                                        <h3 className={classNames("!text-xl")}>{fieldset.label}</h3>
                                    </div>}
                                    <div className={classNames(
                                        fieldset.label ? "col-span-2" : "col-span-3",
                                        "text-left col-span-2"
                                    )}>
                                        <WebsiteFieldRenderer
                                            fields={fieldset.fields}
                                            values={values}/>
                                        {(productFields.length - 1) !== i &&
                                            <hr className="mt-6"/>}
                                    </div>
                                </div>
                            )
                        })}
                        {values.description && (
                            <>
                                {productFields &&
                                    <hr className={"my-6"}/>
                                }
                                <h3 data-title="" className={classNames("!text-xl mt-6")}>Description</h3>
                                <div className={"rich-text"}
                                     dangerouslySetInnerHTML={{__html: values.description}}
                                     data-body=""
                                />
                            </>
                        )}
                        {context?.policies ? (
                            <div className={"pt-6"}>
                                {Object.keys(context.policies).map((key, i) => (
                                    <Fragment key={key}>
                                        {context.policies[key] ?
                                            <Accordion
                                                className={classNames(i === 0 ? "border-t" : null, "border-b border-gray-300")}
                                                title={key.charAt(0).toUpperCase() + key.slice(1)}
                                                text={context.policies[key]}/>
                                            : null}
                                    </Fragment>
                                ))}
                            </div>
                        ) : null}
                        {context?.website?.store?.productFootnote ? (
                            <div className={"pt-8 text-xs text-gray-600 whitespace-pre-line"}>
                                <div className={"rich-text"} dangerouslySetInnerHTML={{__html: context.website.store.productFootnote}}/>
                            </div>
                        ) : null}
                    </div>
                    {/* Purchase details and method sidebar */}
                    <div className={"col-start-3 col-span-1 order-last sm:pt-0 pt-6"}>
                        <div
                            className={"mx-0 rounded-theme section-grey shadow-lg border sm:-mt-[80px] sm:!section-white sm:sticky sm:top-[calc(var(--site-nav-height)+16px)] sm:border-0"}>
                            <div
                                className="text-left section-dark container-padding-half rounded-theme sm:rounded-theme-top sm:block hidden">
                                {/*Show Bidding related UI if we have a sales event*/}
                                {values.salesEvent &&
                                    <>
                                        <BidSalesEvent
                                            profile={profile}
                                            bids={values?.bids}
                                            productUuid={publicProductIdentifier}
                                            salesEvent={values.salesEvent}
                                            onBidSuccess={handleBidSuccess}
                                            onRefreshRequested={refreshSalesEventState}
                                            usersBid={usersBid}
                                            variant="full"
                                            publicBidHistory={publicBidHistory}
                                            isPublicBidHistoryLoading={isPublicBidHistoryLoading}
                                            showPublicBidHistory={shouldShowPublicBidHistory}
                                            onBidHistoryRequested={refreshPublicBidHistory}
                                            isBidderOutcomeReady={isBidderOutcomeReady}
                                        />
                                        {/*Show a horizontal rule under the bidding UI if we have a selling price*/}
                                        {values.sellingPrice > 0 && !values?.productTemplate?.hidePriceOnWebsite ?
                                            <hr className={"mt-4 mb-3 mx-0"}/> : null}
                                    </>
                                }

                                {/*Handle showing sale prices. Hide sale price if it's 0 and there's a sales event*/}
                                {!values?.productTemplate?.hidePriceOnWebsite && (!values.salesEvent || hasSellingPrice) && <>
                                    {values.discountedSellingPrice ? (
                                        <span className={classNames("text-2xl font-semibold leading-none")}>
                                                    <Currency.DisplayValue
                                                        country={context.country}
                                                        currency={context.currency}
                                                        value={values.discountedSellingPrice}
                                                        emptyPlaceholder={""}
                                                    />
                                                </span>
                                    ) : null}
                                    <span
                                        className={classNames("text-2xl leading-none", values.discountedSellingPrice ? "text-xl line-through ml-2 opacity-80" : "font-semibold")}>
                                                <Currency.DisplayValue
                                                    country={context.country}
                                                    currency={context.currency}
                                                    value={values.sellingPrice || ""}
                                                    emptyPlaceholder={"Price on Application"}
                                                />
                                            </span>
                                    {values.sellingPriceDisclaimer ? (
                                        <div className={"leading-tight"}>
                                            {values.sellingPriceDisclaimer}
                                        </div>
                                    ) : null}
                                </>}
                                {values?.productTemplate?.hidePriceOnWebsite && !values.salesEvent && <>
                                        <span
                                            className={classNames("text-2xl leading-none", values.discountedSellingPrice ? "text-xl line-through ml-2 opacity-80" : "font-semibold")}>
                                               Enquire
                                            </span>
                                </>}
                                {values?.productTemplate?.pricingFields && values?.productTemplate?.pricingFields.length > 0 ? (
                                    <>
                                        <hr className="my-4 mx-0 w-full"/>
                                        <PricingFields
                                            pricingFields={values.productTemplate.pricingFields}
                                            productFields={flattendedProductFields}
                                            productValues={values}
                                        />
                                    </>
                                ) : null}
                            </div>
                            {/*Container for enquiry purchase method*/}
                            <div
                                className={"max-h-none sm:overflow-y-auto text-left container-padding-half sm:max-h-[calc(100vh-128px-var(--site-nav-height))] "}>
                                {purchaseMethod === 'enquiry' ? (
                                    <>
                                        <h2 data-title="" className={"!text-2xl text-center sm:hidden block"}>Enquire</h2>
                                        <EnquiryPurchaseMethod {...props} />
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Bottom sticky mobile purchase method banner */}
            <ProductCtaContainer
                floatingNoticeObstacle
                className={classNames(
                    "sticky container-padding-half h-auto min-h-20 items-stretch",
                    isScrolledUnder && !isKeyboardOpen ? "bottom-0" : "-bottom-20"
                )}
            >
                {/*Hide the enquiry (and any future purchase methods implementations) if there is a sales event*/}
                {purchaseMethod === 'enquiry' && !values.salesEvent ? (
                    <EnquiryActionBanner
                        {...props}
                        context={context}
                    />) : null}
                {/*If there is a sales event lets show that instead*/}
                {values.salesEvent &&
                    <div className={"flex flex-row w-full justify-between items-center"}>
                        <BidSalesEvent
                            profile={profile}
                            bids={values?.bids}
                            productUuid={publicProductIdentifier}
                            salesEvent={values.salesEvent}
                            onBidSuccess={handleBidSuccess}
                            onRefreshRequested={refreshSalesEventState}
                            usersBid={usersBid}
                            variant="compact"
                            bidButtonClasses={"shrink-0 w-auto h-12"}
                            publicBidHistory={publicBidHistory}
                            isPublicBidHistoryLoading={isPublicBidHistoryLoading}
                            showPublicBidHistory={shouldShowPublicBidHistory}
                            onBidHistoryRequested={refreshPublicBidHistory}
                            isBidderOutcomeReady={isBidderOutcomeReady}
                        />
                    </div>
                }
            </ProductCtaContainer>
            </Block>
        </Gallery>
    )
}
